import { useMemo } from 'react';
import * as React from 'react';

import { Typography } from '@mui/material';

import {
  Alert,
  DataGridPremium,
  GridColDef,
  Stack,
} from '@sbv-clients/ui-shared';
import {
  formatDateTimeString,
  getApiErrorMessage,
  nameOfFactory,
} from '@sbv-clients/util-misc';

import {
  CallOffAbsenceType,
  useCallOffsReportGet,
} from '../../../../../services/dashboard';

interface TableProps {
  callOffResponse: ReturnType<typeof useCallOffsReportGet>;
}

const mapToRows = (data: CallOffAbsenceType[]) => {
  return data.map((item, index) => ({
    ...item,
    id: index,
  }));
};

const rowKey = nameOfFactory<CallOffAbsenceType>();

const CallOffsGrid = ({ callOffResponse }: TableProps) => {
  const { isError, error } = callOffResponse;
  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: rowKey('absenceDate'),
        headerName: 'Day',
        flex: 1,
        renderCell: (params) => formatDateTimeString(params.row.absenceDate),
      },
      {
        field: rowKey('shiftTypeName'),
        headerName: 'Shift Type',
        flex: 1,
        renderCell: (params) =>
          `${params.row.positionName}/${params.row.shiftTypeName}`,
      },
      {
        field: rowKey('employeeName'),
        headerName: 'Employee',
        flex: 1,
      },
      {
        field: rowKey('absenceReason'),
        headerName: 'Reason',
        flex: 1,
      },
    ],
    []
  );

  const rows = useMemo(
    () => mapToRows(callOffResponse?.data?.absences || []),
    [callOffResponse?.data?.absences]
  );

  const noRowsOverlay = useMemo(() => {
    return !isError
      ? () => (
          <Stack height={'85%'} justifyContent={'center'} alignItems={'center'}>
            <Typography>No data found!</Typography>
          </Stack>
        )
      : () => (
          <Stack height={'85%'} justifyContent={'center'} alignItems={'center'}>
            <Alert color={'error'} variant={'outlined'}>
              <Typography>{getApiErrorMessage(error)}</Typography>
            </Alert>
          </Stack>
        );
  }, [error, isError]);

  return (
    <DataGridPremium
      hideFooter
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      loading={callOffResponse?.isLoading}
      slots={{ noRowsOverlay }}
    />
  );
};

export default CallOffsGrid;
